import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Link as ChakraLink,
  LinkProps as ChakraLinkProps,
  Spacer,
} from '@chakra-ui/react';
import { forwardRef } from '@chakra-ui/react';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';

import { isExternalLink } from '../../utils/link.utils';

interface LinkProps extends ChakraLinkProps {
  to: string;
  arrow?: boolean;
  variant?: 'plain' | 'underline';
}

interface LinkWrapperProps {
  group: boolean;
  width?: ChakraLinkProps['width'];
}

const LinkArrow: React.FC = () => (
  <ChevronRightIcon
    sx={{
      transition: 'all 0.4s ease-in-out',
      _groupHover: {
        transform: 'translate(0.75em,0)',
      },
    }}
    boxSize="3em"
    color="gray.600"
  />
);

const LinkWrapper: React.FC<LinkWrapperProps> = ({
  group,
  width,
  children,
}) => {
  if (group) {
    return (
      <Box as="span" width={width || '100%'} role="group">
        {children}
      </Box>
    );
  }
  return <>{children}</>;
};

const Link = forwardRef<LinkProps, 'a'>(
  (
    { to, children, arrow, alignItems = 'center', variant, width, ...rest },
    ref,
  ) => {
    const hasChildren = React.Children.count(children) > 1;
    const sharedProps: React.ComponentProps<typeof ChakraLink> = {
      display: hasChildren || arrow ? 'inline-flex' : undefined,
      flexDirection: arrow ? 'row' : 'column',
      alignItems,
      variant: variant || (arrow ? 'plain' : undefined),
      width,
      ref,
      ...rest,
    };

    if (isExternalLink(to)) {
      return (
        <LinkWrapper group={Boolean(arrow)} width={width}>
          <ChakraLink href={to} {...sharedProps} isExternal>
            {children}
            {arrow && (
              <>
                <Spacer as="span" />
                <LinkArrow />
              </>
            )}
          </ChakraLink>
        </LinkWrapper>
      );
    }

    // TODO Remove hard references to Gatsby implementations - allow consumer to provide custom internal link component
    //      to support alternative router-based frameworks (React-Router, etc)
    return (
      <LinkWrapper group={Boolean(arrow)} width={width}>
        <ChakraLink as={GatsbyLink} to={to} {...sharedProps}>
          {children}
          {arrow && (
            <>
              <Spacer as="span" />
              <LinkArrow />
            </>
          )}
        </ChakraLink>
      </LinkWrapper>
    );
  },
);

export default Link;
